import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { PageHeader, Card, Btn, Alert, colors, spacing, typography, radius, textareaStyle, selectStyle } from '../design-system';

// ── Helpers ────────────────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function VerdictBadge({ verdict, confidence }) {
  const cfg = {
    pass:    { color: '#059669', bg: '#dcfce7', border: '#86efac', label: 'Pass' },
    partial: { color: '#d97706', bg: '#fef3c7', border: '#fde047', label: 'Partial' },
    fail:    { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', label: 'Fail' },
    error:   { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', label: 'Error' },
  }[verdict] || { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', label: verdict };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: spacing.md,
      padding: `${spacing.md} ${spacing.lg}`,
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: radius.md,
      marginBottom: spacing.lg,
    }}>
      <div>
        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cfg.color, marginBottom: 2 }}>Verdict</div>
        <div style={{ fontSize: '26px', fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{cfg.label}</div>
      </div>
      {confidence !== undefined && (
        <div style={{ borderLeft: `1px solid ${cfg.border}`, paddingLeft: spacing.md }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cfg.color, marginBottom: 2 }}>Confidence</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{confidence}%</div>
        </div>
      )}
      <div style={{ flex: 1, paddingLeft: spacing.md, borderLeft: `1px solid ${cfg.border}` }}>
        <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: cfg.color, marginBottom: 4 }}>Summary</div>
        <div style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, lineHeight: 1.5 }}>{summary}</div>
      </div>
    </div>
  );
}

function ResultSection({ title, items, accentColor }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.sm }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: spacing.sm, padding: `8px ${spacing.sm}`, background: colors.background, borderRadius: radius.sm, borderLeft: `3px solid ${accentColor}` }}>
            <div style={{ width: 4, flexShrink: 0 }} />
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary, lineHeight: 1.5 }}>{item}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClauseTag({ clause }) {
  return (
    <span style={{
      display: 'inline-block',
      background: '#eff6ff', color: '#1e40af',
      border: '1px solid #bfdbfe',
      borderRadius: radius.sm,
      fontSize: typography.fontSizes.xs, fontWeight: 700,
      padding: '2px 8px',
      marginRight: 6, marginBottom: 4,
      fontFamily: 'monospace',
    }}>
      {clause}
    </span>
  );
}

// ── Review Result ──────────────────────────────────────────────────────────────

function ReviewResult({ result }) {
  const verdict    = result.verdict;
  const confidence = result.confidence;
  const summary    = result.summary;
  const cfg = {
    pass:    { color: '#059669', bg: '#dcfce7', border: '#86efac', label: 'Pass' },
    partial: { color: '#d97706', bg: '#fef3c7', border: '#fde047', label: 'Partial' },
    fail:    { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', label: 'Fail' },
    error:   { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', label: 'Error' },
  }[verdict] || { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', label: verdict };

  return (
    <div>
      {/* Verdict banner */}
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 0,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        borderRadius: radius.md, marginBottom: spacing.lg, overflow: 'hidden',
      }}>
        <div style={{ padding: `${spacing.md} ${spacing.lg}`, borderRight: `1px solid ${cfg.border}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 100, textAlign: 'center' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cfg.color, marginBottom: 4 }}>Verdict</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{cfg.label}</div>
        </div>
        {confidence !== undefined && (
          <div style={{ padding: `${spacing.md} ${spacing.lg}`, borderRight: `1px solid ${cfg.border}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 90, textAlign: 'center' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cfg.color, marginBottom: 4 }}>Confidence</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{confidence}%</div>
          </div>
        )}
        <div style={{ padding: `${spacing.md} ${spacing.lg}`, flex: 1 }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: cfg.color, marginBottom: 4 }}>Summary</div>
          <div style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, lineHeight: 1.5 }}>{summary}</div>
        </div>
      </div>

      <ResultSection title="Strengths"       items={result.strengths}        accentColor="#10b981" />
      <ResultSection title="Gaps Identified" items={result.gaps}             accentColor="#ef4444" />
      <ResultSection title="Recommendations" items={result.recommendations}  accentColor="#3b82f6" />

      {result.relevant_clauses && result.relevant_clauses.length > 0 && (
        <div style={{ marginBottom: spacing.lg }}>
          <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.sm }}>
            Relevant Clauses
          </div>
          <div>
            {result.relevant_clauses.map((c, i) => <ClauseTag key={i} clause={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Requirement Picker ─────────────────────────────────────────────────────────

function RequirementPicker({ requirements, onSelect }) {
  const [open, setOpen] = useState(false);
  const grouped = requirements.reduce((acc, r) => {
    const key = r.standard || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div style={{ marginBottom: spacing.md }}>
      <Btn variant="secondary" size="sm" onClick={() => setOpen(o => !o)}>
        {open ? 'Hide requirement library' : 'Browse requirement library'}
      </Btn>
      {open && (
        <div style={{
          marginTop: spacing.sm,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          maxHeight: 320, overflowY: 'auto',
          background: colors.surface,
        }}>
          {Object.entries(grouped).map(([std, reqs]) => (
            <div key={std}>
              <div style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.background, fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                {std}
              </div>
              {reqs.map(r => (
                <div
                  key={r.id}
                  onClick={() => { onSelect(r); setOpen(false); }}
                  style={{ padding: `10px ${spacing.md}`, cursor: 'pointer', borderBottom: `1px solid ${colors.border}`, fontSize: typography.fontSizes.sm, lineHeight: 1.5, transition: 'background 100ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.background}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.primary, marginRight: 8 }}>{r.clause}</span>
                  {r.text}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── History ────────────────────────────────────────────────────────────────────

function HistoryItem({ item, onRestore }) {
  const cfg = {
    pass: '#10b981', partial: '#f59e0b', fail: '#ef4444',
  }[item.verdict] || '#64748b';

  return (
    <div
      onClick={() => onRestore(item)}
      style={{
        padding: spacing.md, borderRadius: radius.md,
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${cfg}`,
        cursor: 'pointer', marginBottom: spacing.sm,
        transition: 'background 100ms',
      }}
      onMouseEnter={e => e.currentTarget.style.background = colors.background}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, lineHeight: 1.3 }}>
          {item.standard} — {item.requirement?.slice(0, 60)}…
        </div>
        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: cfg, marginLeft: spacing.sm }}>
          {item.verdict}
        </span>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AIEvidenceReview() {
  const [form, setForm] = useState({ requirement: '', evidence_description: '', standard: '', context: '' });
  const [requirements, setRequirements] = useState([]);
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [history,  setHistory]  = useState([]);
  const [msg,      setMsg]      = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    axiosClient.get('/ai-review/requirements')
      .then(r => setRequirements(r.data))
      .catch(() => {});
    const saved = localStorage.getItem('ai_review_history');
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch {}
    }
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.requirement.trim() || !form.evidence_description.trim()) {
      flash('Please enter both a requirement and evidence description.', 'error');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axiosClient.post('/ai-review/', form);
      setResult(res.data);
      const newHistory = [res.data, ...history].slice(0, 10);
      setHistory(newHistory);
      localStorage.setItem('ai_review_history', JSON.stringify(newHistory));
    } catch (err) {
      flash(err?.response?.data?.detail || 'Review failed. Check ANTHROPIC_API_KEY is configured.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const restoreFromHistory = (item) => {
    setForm({ requirement: item.requirement || '', evidence_description: item.evidence_description || '', standard: item.standard || '', context: '' });
    setResult(item);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="AI Evidence Review"
        subtitle="Use Claude to analyse submitted evidence against ISO accreditation requirements"
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, alignItems: 'start' }}>
        {/* Input panel */}
        <div>
          <Card>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.lg }}>
              Evidence to Review
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div>
                <FieldLabel>Standard</FieldLabel>
                <select style={selectStyle} value={form.standard} onChange={e => set('standard', e.target.value)}>
                  <option value="">General ISO accreditation</option>
                  <option value="ISO 17011">ISO 17011 — Accreditation bodies</option>
                  <option value="ISO 9001">ISO 9001 — Quality Management</option>
                  <option value="ISO 14001">ISO 14001 — Environmental</option>
                  <option value="ISO 45001">ISO 45001 — Health & Safety</option>
                  <option value="ISO 27001">ISO 27001 — Information Security</option>
                </select>
              </div>

              <div>
                <FieldLabel>Requirement being assessed</FieldLabel>
                <RequirementPicker requirements={requirements} onSelect={r => { set('requirement', r.text); set('standard', r.standard); }} />
                <textarea
                  style={{ ...textareaStyle, minHeight: 80 }}
                  value={form.requirement}
                  onChange={e => set('requirement', e.target.value)}
                  placeholder="Paste or type the specific requirement clause…"
                />
              </div>

              <div>
                <FieldLabel>Evidence submitted</FieldLabel>
                <textarea
                  style={{ ...textareaStyle, minHeight: 140 }}
                  value={form.evidence_description}
                  onChange={e => set('evidence_description', e.target.value)}
                  placeholder="Describe the evidence in detail — document names, content summaries, dates, what processes are covered, any gaps you've noticed…"
                />
              </div>

              <div>
                <FieldLabel>Additional context (optional)</FieldLabel>
                <textarea
                  style={{ ...textareaStyle, minHeight: 60 }}
                  value={form.context}
                  onChange={e => set('context', e.target.value)}
                  placeholder="Organisation type, size, sector, previous findings…"
                />
              </div>

              <Btn variant="primary" size="md" onClick={submit} loading={loading} disabled={!form.requirement || !form.evidence_description}>
                {loading ? 'Analysing…' : 'Analyse Evidence'}
              </Btn>
            </div>
          </Card>

          {/* History */}
          {history.length > 0 && (
            <div style={{ marginTop: spacing.lg }}>
              <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: spacing.sm }}>
                Recent Reviews
              </div>
              {history.map((item, i) => <HistoryItem key={i} item={item} onRestore={restoreFromHistory} />)}
            </div>
          )}
        </div>

        {/* Result panel */}
        <div>
          {loading && (
            <Card style={{ textAlign: 'center', padding: spacing.xl }}>
              <div style={{ fontSize: typography.fontSizes.md, color: colors.textMuted, marginBottom: spacing.sm }}>Analysing evidence…</div>
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>Claude is reviewing against the requirement. This usually takes 5–10 seconds.</div>
            </Card>
          )}

          {!loading && !result && (
            <Card style={{ padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, marginBottom: spacing.sm }}>
                Ready to review
              </div>
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, lineHeight: 1.6 }}>
                Enter a requirement and the evidence submitted for it, then click Analyse. Claude will assess whether the evidence satisfies the requirement and highlight any gaps.
              </div>
            </Card>
          )}

          {!loading && result && (
            <Card>
              <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.lg }}>
                Analysis Result
              </div>
              <ReviewResult result={result} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
